import { InvoicePackagePropagation } from 'types';

/**
 * Copy for the invoice edit that staff chose to apply to the package.
 *
 * The prompt promises the change reaches the rest of the package, so the confirmation has to say
 * whether it did. A package that refused is a live package left on the old setting, which is the
 * exact failure this feature exists to remove, and staff asked for it by name.
 */

export type ApplyScopeField = 'auto_pay' | 'payment_method';

const FIELD_LABELS: Record<string, string> = {
  auto_pay: 'Auto-Pay',
  payment_method: 'payment method',
};

// Reads as the tail of "The package was not changed because ...", so each clause takes the
// package as its subject. The fallback keeps a raw error code away from a gym owner.
const REFUSAL_LABELS: Record<string, string> = {
  NO_PACKAGE: 'this invoice is not tied to a package',
  PACKAGE_UNAVAILABLE: 'it could not be found',
  AUTO_PAY_NOT_CHANGEABLE: 'it is no longer active',
  PAYMENT_METHOD_NOT_CHANGEABLE: 'it is no longer active',
  AUTO_PAY_REQUIRES_PAYMENT_METHOD: 'it has no card on file',
  AUTO_PAY_REQUIRES_STRIPE_PAYMENT_METHOD: 'it is set to cash or check',
  AUTO_PAY_BLOCKS_NON_STRIPE_PAYMENT_METHOD: 'it still has auto-pay on',
};
const REFUSAL_FALLBACK = 'it could not take the change';

// A chain member the change reached the package but not this row. Deliberately not imported from
// the package Actions menu's own copy file: that lives under pages/ and phrases these as the tail
// of a different sentence. Same two grounds, named for this sentence.
const SKIP_REASON_LABELS: Record<string, string> = {
  NO_PAYMENT_METHOD: 'no card on file',
  NON_STRIPE_PAYMENT_METHOD: 'set to cash or check',
};

/** Names the fields in the prompt, so it reads as one question about one membership. */
export const applyScopePromptSubject = (changedFields: string[]): string => {
  const labels = changedFields.map(field => FIELD_LABELS[field] ?? field);
  if (labels.length === 0) {
    return 'this change';
  }
  return labels.length === 1 ? `the ${labels[0]} change` : `the ${labels.join(' and ')} changes`;
};

/**
 * What the package option in the prompt promises, which is not the same for both fields.
 *
 * Auto-pay stops at upcoming invoices, since nothing collects on the flag outside that status,
 * while the payment method reaches every unpaid one. Saying "unpaid" for an auto-pay change would
 * promise more than the backend writes, which is the same defect as the silent narrowing this
 * feature removes.
 */
export const applyScopePackageOptionDescription = (changedFields: string[]): string => {
  // Names every status the chain walk reads, which is CURRENT_STATUSES: active, upcoming and on
  // hold. Saying "active and upcoming" understates the reach, the same way the Auto-Pay and
  // Change Payment Method modals did before they were corrected.
  const lead = 'The package, the other active, upcoming and on-hold packages linked to it, and their';
  const reachesAutoPay = changedFields.includes('auto_pay');
  const reachesPaymentMethod = changedFields.includes('payment_method');

  if (reachesAutoPay && reachesPaymentMethod) {
    return `${lead} invoices also take the change: upcoming invoices for Auto-Pay, every unpaid invoice for the payment method.`;
  }
  if (reachesPaymentMethod) {
    return `${lead} unpaid invoices also take the change.`;
  }
  return `${lead} upcoming invoices also take the change.`;
};

const capitalise = (sentence: string): string => sentence.charAt(0).toUpperCase() + sentence.slice(1);

export const invoiceUpdateSuccessMessage = (
  propagation: InvoicePackagePropagation | null | undefined,
  changedFields: ApplyScopeField[] = [],
): string => {
  if (!propagation) {
    return 'Invoice updated successfully';
  }

  const { appliedFields, refusalCode, refusalMessage, chainPropagation } = propagation;
  const sentences = ['Invoice updated successfully.'];

  if (appliedFields.length > 0) {
    // Counts packages, not invoices. An invoice with a charge already in flight keeps its old
    // values, so naming invoices here would assert more than was written.
    const packageCount = chainPropagation?.packagesUpdated ?? 1;
    const skippedCount = chainPropagation?.packagesSkipped ?? 0;
    const subject = applyScopePromptSubject(appliedFields);

    if (skippedCount > 0) {
      // A skipped chain member is a live package that will renew on the old setting, which is the
      // exact failure this feature exists to remove. The prompt just told staff the change
      // reaches the rest of the package, so leaving this out would make it a promise we broke.
      const labels = (chainPropagation?.skippedReasons ?? [])
        .map(reason => SKIP_REASON_LABELS[reason] ?? 'not eligible')
        .join(' or ');
      const noun = skippedCount === 1 ? 'package was skipped' : 'packages were skipped';
      sentences.push(
        `Applied ${subject} to ${packageCount} of ${packageCount + skippedCount} packages.`,
        `${skippedCount === 1 ? 'One' : skippedCount} ${noun}: ${labels}.`,
      );
    } else {
      const reach = packageCount > 1 ? `${packageCount} packages` : 'this package';
      sentences.push(`Applied ${subject} to ${reach}.`);
    }
  }

  if (refusalCode) {
    const reason = REFUSAL_LABELS[refusalCode] ?? REFUSAL_FALLBACK;
    // Scoped to the fields that did not land, so a partial application does not contradict
    // itself. Saying "the package was not changed" alongside "applied the payment method change
    // to this package" tells staff two opposite things about the same save.
    const refusedFields = changedFields.filter(field => !appliedFields.includes(field));
    // The server writes a message only where the code cannot carry the detail, and the one that
    // does names the package blocking the change, which chain propagation means need not be the
    // one on screen. It replaces the coded clause rather than joining it: the two say the same
    // thing, and the message says which package. This is the sentence the package Actions menu
    // already shows for the same refusal.
    const lead =
      appliedFields.length > 0 && refusedFields.length > 0
        ? `${capitalise(applyScopePromptSubject(refusedFields))} was not applied to the package`
        : 'The package was not changed';
    sentences.push(refusalMessage ? `${lead}. ${refusalMessage}` : `${lead} because ${reason}.`);
  }

  return sentences.join(' ');
};
